import type { MetaFunction } from 'react-router';
import type { loader } from '~/routes/_app.product.$urlSegment';
import type { Offer, OfferShippingDetails, Product } from 'schema-dts';
import { decodeHTMLString } from '../util';
import { getMetaBaseUrl } from '../util/create-meta-base-url';
import type { loader as rootLoader } from '~/root';
import type { loader as appLoader } from '~/routes/_app';
import { makeVariantPrice } from '~/modules/product/util/make-variant-price';
import { variantHasPrice } from '~/modules/product/util/variant-has-price';

export const createProductSchema: MetaFunction<
    typeof loader,
    {
        root: typeof rootLoader;
        'routes/_app': typeof appLoader;
    }
> = (props) => {
    const { data } = props;
    if (!data) return [];
    const { product } = data;

    const baseUrl = getMetaBaseUrl(props);

    if (product.CanPurchase === undefined) {
        return [];
    }

    const contentString =
        data.localisation?.Description ??
        product.Content?.replace(/(<([^>]+)>)/gi, '');

    const defaultCountry = product.Professional?.Country
        ? product.Professional?.Country + 'D'
        : 'NZD';
    const priceVariantCurrencyID =
        !!product.PricingDisplay && 'ReferenceVariant' in product.PricingDisplay
            ? product.PricingDisplay?.ReferenceVariant?.ID
            : undefined;
    const priceCurrency =
        product.Variants.find(
            (variant) => variant.ID === priceVariantCurrencyID,
        )?.Price.Currency ?? defaultCountry;

    const selectedVariant =
        product.Variants.find(
            (variant) => variant.ID === priceVariantCurrencyID,
        ) ??
        product.Variants[0] ??
        product.MasterVariant ??
        null;

    const hasPrice = selectedVariant
        ? variantHasPrice(selectedVariant, product.Pricing.PricingType)
        : false;

    const { isPriceRange, maxPrice, price } = makeVariantPrice(
        hasPrice,
        selectedVariant,
        product.Pricing.PricingType,
    );

    const showMaxPrice =
        isPriceRange &&
        maxPrice.Amount > 0 &&
        price.Amount > 0 &&
        maxPrice.Amount !== price.Amount;

    const priceToShow = isPriceRange
        ? price.Amount > 0
            ? price
            : maxPrice
        : price;

    const amount = priceToShow?.Amount > 0 ? priceToShow.Amount : 0;
    const priceValue = amount / 100;
    const shippingProfile = product.Variants.find(
        (variant) => variant.ShippingProfile,
    );

    const productSchema = constructProductSchema({
        id: product.ID,
        title: product.Title,
        description: contentString,
        url: baseUrl + product.Link,
        image:
            baseUrl +
                product.DefaultImage?.LibraryImageFile?.DetailPageSquare
                    ?.Desktop ||
            baseUrl + product.FacebookPreviewImage ||
            '',
        professionalName: product.Professional?.Title,
        price: {
            price: priceValue.toFixed(2),
            maxPrice: showMaxPrice
                ? (maxPrice.Amount / 100).toFixed(2)
                : priceValue.toFixed(2),
            hasPriceRange: showMaxPrice,
        },
        variantCount: product.Variants.length || 1,
        priceCurrency,
        shippingDetails: {
            '@type': 'OfferShippingDetails',
            doesNotShip: shippingProfile?.ShippingProfile?.NoShipping,
        },
    });

    return [productSchema];
};

interface ProductSchemaProps {
    id: number;
    title: string;
    description?: string;
    url: string;
    image: string;
    professionalName: string;
    priceCurrency: string;
    price: {
        price?: string;
        maxPrice?: string;
        hasPriceRange?: boolean;
    };
    variantCount: number;
    shippingDetails?: OfferShippingDetails;
}

/**
 * Generate an LD+JSON schema for a product. The schema will be read by Google
 * Merchant Center and similar services to augment search results.
 */
export const constructProductSchema = (
    product: ProductSchemaProps,
): Product => {
    return {
        '@type': 'Product',
        productID: 'product_' + product.id,
        name: decodeHTMLString(product.title),
        description: product.description,
        url: product.url,
        image: product.image,
        brand: decodeHTMLString(product.professionalName) ?? 'ArchiPro',
        offers: [constructAggregateOfferSchema(product)],
    };
};

/**
 * Generate an LD+JSON schema for an product offer. An offer is a sub part of a
 * product schema containing information about the price and availability of the
 * product.
 */
const constructAggregateOfferSchema = (product: ProductSchemaProps): Offer => {
    return {
        price: product.price.price ?? '0.00',
        priceCurrency: product.priceCurrency ?? 'NZD',
        itemCondition: 'https://schema.org/NewCondition',
        availability: 'https://schema.org/InStock',
        '@type': 'Offer',
        priceSpecification: [
            {
                '@type': 'PriceSpecification',
                price: product.price.price ?? '0.00',
                priceCurrency: product.priceCurrency ?? 'NZD',
                ...(product.price.hasPriceRange
                    ? {
                          minPrice: Number(product.price.price ?? '0.00'),
                          maxPrice: Number(product.price.maxPrice),
                      }
                    : {}),
            },
        ],
    };
};
